'use client'

// Oferta del premio de visitas en el carrito de la tienda online (PR 5b).
//
// Solo con `enabled` (el local tiene `features.visit_rewards === true`): sin él no
// monta nada y NO hace ninguna petición — la tienda de main. Con él consulta
// `/api/visit-rewards/my-card` (tope de 800 ms: el carrito nunca espera) y pinta:
//   - premio disponible y no `held` → «Tienes {n} {premio} gratis · Usar»
//   - premio aplicado, con línea elegible → «−$X {premio} gratis»
//   - premio aplicado, sin línea elegible → «Agrega una {premio} para usarla»
//   - sin sesión → «¿Tienes premios? Entra a tu cuenta»
//   - falla o tarda de más → nada.
// El monto es vista previa: `/session` lo recomputa y manda. La lógica pura vive en
// lib/microsite/visitRewardUx.ts; aquí se pinta (mismo estilo que el campo de cupón
// del carrito: es el micrositio público, no el admin).

import { useEffect, useState } from 'react'
import { Gift, X } from 'lucide-react'
import { useTranslations } from 'next-intl'
import {
  availableRewards,
  fetchMyCardWithTimeout,
  type AppliedVisitReward,
  type MyCardResult,
  type VisitRewardNotice,
} from '@/lib/microsite/visitRewardUx'

export interface VisitRewardWebOfferProps {
  /** `features.visit_rewards === true`. false = no monta nada ni consulta. */
  enabled: boolean
  locationId: string
  accentColor: string
  applied: AppliedVisitReward | null
  /** Monto vista previa del premio aplicado; null = ninguna línea elegible. */
  discount: number | null
  notice: VisitRewardNotice | null
  onApply: (reward: AppliedVisitReward) => void
  onRemove: () => void
  onDismissNotice: () => void
}

export function VisitRewardWebOffer(props: VisitRewardWebOfferProps) {
  if (!props.enabled) return null
  return <VisitRewardWebOfferLoaded {...props} />
}

function VisitRewardWebOfferLoaded(props: VisitRewardWebOfferProps) {
  const { locationId } = props
  const [my, setMy] = useState<MyCardResult | null>(null)
  const [signInHref, setSignInHref] = useState('/cuenta/entrar')

  useEffect(() => {
    let alive = true
    void fetchMyCardWithTimeout(locationId).then((r) => {
      if (!alive) return
      // La URL actual solo se conoce en el navegador: se lee aquí, con la respuesta.
      setSignInHref(`/cuenta/entrar?next=${encodeURIComponent(window.location.pathname + window.location.search)}`)
      setMy(r)
    })
    return () => {
      alive = false
    }
  }, [locationId])

  return <VisitRewardWebOfferView {...props} my={my} signInHref={signInHref} />
}

export function VisitRewardWebOfferView({
  my,
  signInHref,
  accentColor,
  applied,
  discount,
  notice,
  onApply,
  onRemove,
  onDismissNotice,
}: Omit<VisitRewardWebOfferProps, 'enabled' | 'locationId'> & { my: MyCardResult | null; signInHref: string }) {
  const t = useTranslations('public.onlineOrder.checkout.visitReward')
  const label = applied?.label ?? (my?.kind === 'offer' ? my.card.rewardLabel : '')

  let body: React.ReactNode = null
  if (applied) {
    body = (
      <div className="flex items-center justify-between gap-2" data-testid="visit-reward-applied">
        <span className="text-sm font-semibold" style={{ color: accentColor }}>
          <Gift className="inline w-4 h-4 mr-1.5 align-[-2px]" />
          {discount != null
            ? t('applied', { amount: `$${discount.toFixed(2)}`, label: applied.label })
            : t('addOne', { label: applied.label })}
        </span>
        <button
          type="button"
          onClick={onRemove}
          aria-label={t('removeAria')}
          className="text-xs font-semibold text-gray-500 underline"
        >
          {t('remove')}
        </button>
      </div>
    )
  } else if (my?.kind === 'offer') {
    const free = availableRewards(my.card)
    if (free.length > 0 && my.eligibleItemIds.length > 0) {
      body = (
        <div className="flex items-center justify-between gap-2" data-testid="visit-reward-offer">
          <span className="text-sm font-semibold" style={{ color: accentColor }}>
            <Gift className="inline w-4 h-4 mr-1.5 align-[-2px]" />
            {t('offer', { n: free.length, label: my.card.rewardLabel })}
          </span>
          <button
            type="button"
            onClick={() =>
              onApply({ rewardId: free[0].id, label: my.card.rewardLabel, eligibleItemIds: my.eligibleItemIds })
            }
            className="rounded-full px-3 py-1 text-sm font-bold text-white"
            style={{ backgroundColor: accentColor }}
          >
            {t('use')}
          </button>
        </div>
      )
    }
  } else if (my?.kind === 'signed_out') {
    body = (
      <a href={signInHref} className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: accentColor }} data-testid="visit-reward-signin">
        <Gift className="w-4 h-4" />
        {t('signIn')}
      </a>
    )
  }

  if (!body && !notice) return null
  return (
    <div className="mb-3 space-y-1.5" data-testid="visit-reward-web">
      {notice && (
        <div role="status" className="flex items-start justify-between gap-2 text-xs text-gray-600" data-testid="visit-reward-notice">
          <span>
            {t(`notice.${notice.kind}`, {
              label,
              phoneLast4: notice.kind === 'phone_mismatch' ? notice.phoneLast4 : '',
            })}
          </span>
          <button type="button" onClick={onDismissNotice} aria-label={t('dismissAria')}>
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {body}
    </div>
  )
}
